"use client"

import React, { useEffect, useRef, useState } from 'react'
import { Button } from "../../ui/button"
import { Send, Sparkles, Play, X } from 'lucide-react'

import { SignedIn, SignedOut } from '../../auth/AuthProvider'
import { useNavigate } from 'react-router-dom'

interface FinalCTAProps {
  onGetStartedClick?: (event?: React.MouseEvent) => void
}

export function FinalCTA({ onGetStartedClick }: FinalCTAProps) {
  const navigate = useNavigate()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const gridSize = 30
    const dots: { x: number; y: number; opacity: number; baseOpacity: number }[] = []
    for (let x = 0; x < canvas.width; x += gridSize) {
      for (let y = 0; y < canvas.height; y += gridSize) {
        dots.push({ x, y, opacity: Math.random() * 0.3, baseOpacity: Math.random() * 0.3 })
      }
    }

    let mouseX = 0
    let mouseY = 0
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouseX = e.clientX - rect.left
      mouseY = e.clientY - rect.top
    }
    canvas.addEventListener('mousemove', handleMouseMove)

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      dots.forEach(dot => {
        const distance = Math.sqrt((mouseX - dot.x) ** 2 + (mouseY - dot.y) ** 2)
        const maxDistance = 100
        if (distance < maxDistance) {
          dot.opacity = Math.min(0.8, dot.baseOpacity + (1 - distance / maxDistance) * 0.5)
        } else {
          dot.opacity = Math.max(dot.baseOpacity, dot.opacity - 0.02)
        }
        ctx.fillStyle = `rgba(99, 102, 241, ${dot.opacity})`
        ctx.beginPath()
        ctx.arc(dot.x, dot.y, 2, 0, Math.PI * 2)
        ctx.fill()
        dots.forEach(otherDot => {
          const dotDistance = Math.sqrt((dot.x - otherDot.x) ** 2 + (dot.y - otherDot.y) ** 2)
          if (dotDistance < gridSize * 1.5 && dotDistance > 0) {
            const lineOpacity = Math.max(0, (dot.opacity + otherDot.opacity) / 4 - 0.1)
            if (lineOpacity > 0) {
              ctx.strokeStyle = `rgba(99, 102, 241, ${lineOpacity})`
              ctx.lineWidth = 1
              ctx.beginPath()
              ctx.moveTo(dot.x, dot.y)
              ctx.lineTo(otherDot.x, otherDot.y)
              ctx.stroke()
            }
          }
        })
      })
      requestAnimationFrame(animate)
    }
    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      canvas.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-60 dark:opacity-40" style={{ mixBlendMode: 'multiply' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="container mx-auto max-w-[1200px] px-10 md:px-[72px] relative z-10">
        <div className="text-center space-y-12">
          <div className="relative inline-block">
            <Sparkles className="h-12 w-12 text-indigo-500 mx-auto animate-pulse" />
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping"></div>
            <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-pink-400 rounded-full animate-ping"></div>
          </div>
          <div className="space-y-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
              What are you <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">waiting for?</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Join the future of AI-powered productivity. Start your conversation with AIRA today.
            </p>
          </div>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <SignedOut>
              <Button
                onClick={() => navigate('/login')}
                className="h-12 w-[180px] rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white transition-all duration-180 hover:scale-[1.02] focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 relative group/btn whitespace-nowrap"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-180 blur-lg -z-10"></div>
                Start with AIRA
                <Send className="ml-2 h-4 w-4" />
              </Button>
            </SignedOut>
            <SignedIn>
              <Button 
                className="h-12 w-[180px] rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white transition-all duration-180 hover:scale-[1.02] focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 relative group/btn whitespace-nowrap"
                onClick={async (e) => {
                  onGetStartedClick?.(e)
                }}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-180 blur-lg -z-10"></div>
                Open Chat
                <Send className="ml-2 h-4 w-4" />
              </Button>
            </SignedIn>
            <Button
              variant="outline"
              className="h-12 w-[180px] rounded-2xl border-2 border-indigo-500/50 dark:border-indigo-400/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all duration-180 hover:scale-[1.02] focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 whitespace-nowrap"
              onClick={() => setIsDemoOpen(true)}
            >
              <Play className="mr-2 h-4 w-4" />
              Watch Demo
            </Button>
          </div>
          <div className="flex items-center justify-center space-x-8 pt-8 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shrink-0"></div>
              <span>Enterprise Ready</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shrink-0"></div>
              <span>Instant Access</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse shrink-0"></div>
              <span>No Setup Required</span>
            </div>
          </div>
        </div>
      </div>

      {isDemoOpen && (
        <>
          {/* Overlay with blur */}
          <div
            className="fixed inset-0 bg-black/70 dark:bg-black/70 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in-0 duration-300"
            onClick={() => setIsDemoOpen(false)}
          >
            {/* Modal content with pattern background */}
            <div
              className="relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-2 border-gray-200/50 dark:border-slate-600/50 rounded-2xl p-8 w-[70vw] h-[70vh] max-w-[70vw] max-h-[70vh] mx-4 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Pattern background - subtle dot grid */}
              <div 
                className="absolute inset-0 opacity-[0.04] dark:opacity-[0.03]"
                style={{
                  backgroundImage: `radial-gradient(circle, currentColor 0.5px, transparent 0.5px)`,
                  backgroundSize: '32px 32px',
                  backgroundPosition: '0 0',
                }}
              />

              {/* Content wrapper */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Close button */}
                <button
                  onClick={() => setIsDemoOpen(false)}
                  className="absolute top-0 right-0 z-20 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Header */}
                <div className="mb-4 text-center flex-shrink-0">
                  <h2 className="text-gray-900 dark:text-white text-2xl font-semibold">Get Started with AIRA</h2>
                </div>

                {/* Iframe container */}
                <div className="flex-1 overflow-hidden flex items-center justify-center min-w-0 min-h-0 p-4">
                  <div className="w-full h-full flex items-center justify-center">
                    <div 
                      className="h-full w-auto" 
                      style={{ 
                        aspectRatio: '16 / 12',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto'
                      }}
                    >
                      <iframe 
                        src="https://scribehow.com/embed/Get_Started_with_AIRA__a40p3c9yRQG93pbZLE0uDw?skipIntro=true&as=video" 
                        width="100%" 
                        height="100%"
                        allow="fullscreen" 
                        style={{ border: 0, borderRadius: '0.5rem', display: 'block' }}
                        className="rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}


